import React, { createContext, useEffect } from 'react';
import appConfig from '../Config/appConfig';
import api from '../Api/api';
import localTheme from '../localData/localTheme.json';

const url = appConfig.APIBaseURL;
const ThemeContext = createContext();

// Client-side/transport failures (no HTTP response reached) — safe to retry.
const isNetworkError = (error) =>
  !error.response && (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ECONNRESET' ||
    error.message === 'Network Error'
  );

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_RETRIES = 2;

const fetchThemeConfig = async () => {
  for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
    try {
      return (await api.get(`${url}/api/v1/config/1`)).data;
    } catch (error) {
      if (attempt <= MAX_RETRIES && isNetworkError(error)) {
        await sleep(1000 * attempt);
        continue;
      }
      throw error;
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const setCssVariables = (variables, prefix = '') => {
    Object.entries(variables || {}).forEach(([key, value]) => {
      const cssVarName = `--${prefix}${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      document.documentElement.style.setProperty(cssVarName, value);
    });
  };
  const setGroupVariables = (group, prefix) => {
    const traverseObject = (obj, path) => {
      Object.entries(obj || {}).forEach(([key, value]) => {
        const currentPath = `${path}-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          traverseObject(value, currentPath);
        } else {
          const cssVarName = `--${currentPath}`;
          document.documentElement.style.setProperty(cssVarName, value);
        }
      });
    };

    traverseObject(group, prefix);
  };

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const config = url ? await fetchThemeConfig() : localTheme;

        if (config?.theme) {
          const { colors, typography } = config.theme;

          setCssVariables(colors);
          setGroupVariables(typography.icon, 'icon');
          setGroupVariables(typography.options, 'options');
          setGroupVariables(typography.container, 'container');
          setGroupVariables(typography.button, 'button');
          setGroupVariables(typography.progress, 'progress');
          setGroupVariables(typography.headline, 'headline');
          setGroupVariables(typography.body, 'body');
          setGroupVariables(typography.image, 'image');
          setGroupVariables(typography.cta, 'cta');
          setGroupVariables(typography.navbar, 'navbar');
        } else {
          console.warn('No theme data found in the API response');
        }
      } catch (error) {
        console.error('Error loading theme data:', error.message);
      }
    };

    fetchTheme();
  }, []);

  // Render children immediately to not delay useAuthentication
  // EPIC launch codes and causing intermittent "Something's wrong" errors.
  return <ThemeContext.Provider value={{}}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
